import { useCallback, useEffect, useState } from "react";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { useCurrentUser, userIsBuero } from "@/lib/auth";
import type { Order } from "@/lib/projects";
import { getOrder, getOrderNotes, type ProjectNote } from "@/lib/projectOverview";
import { NotesSection } from "@/projectOverview/sections/NotesSection";
import { useRealtime } from "@/lib/useRealtime";

export function TimesheetBillingNotesPanel({ orderId }: { orderId: number }) {
  const { user } = useCurrentUser();
  const isBuero = userIsBuero(user);
  const [project, setProject] = useState<Order | null>(null);
  const [notes, setNotes] = useState<ProjectNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [order, orderNotes] = await Promise.all([
        getOrder(orderId),
        getOrderNotes(orderId),
      ]);
      setProject(order);
      setNotes(Array.isArray(orderNotes) ? orderNotes : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Notizen konnten nicht geladen werden.");
      setProject(null);
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  useRealtime({
    scopes: ["auftraege:list"],
    domains: ["auftraege", "projects"],
    events: ["auftraege_changed", "projects_changed"],
    enabled: !!orderId,
    onChange: () => {
      void refetch();
    },
  });

  if (loading && !project) {
    return (
      <div className="eh-ts-billing-notes flex items-center justify-center py-8">
        <Spinner />
        <span className="ml-2 text-sm text-label-secondary">Notizen …</span>
      </div>
    );
  }

  if (error && !project) {
    return (
      <div className="eh-ts-billing-notes">
        <Alert variant="error">{error}</Alert>
      </div>
    );
  }

  if (!project) return null;

  return (
    <div className="eh-ts-billing-notes">
      <NotesSection
        orderId={orderId}
        project={project}
        notes={notes}
        user={user}
        isBuero={isBuero}
        onChanged={() => void refetch()}
      />
    </div>
  );
}
